import { useMemo } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Badge from "@/v2-ui/badge"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import { HUBSPOT_BASE_URL } from "@/v2-backoffice-client/constants"
import LicenseAccountLinkButton from "@/v2-backoffice-client/license/account/view/LicenseAccountLinkButton"
import LicenseAccountLinkPicker from "@/v2-backoffice-client/license/account/view/LicenseAccountLinkPicker"
import type { LicenseAccountFragment } from "@/v2-backoffice-client/license/account/__types__/license.account.fragment"
import type { LicenseAccountMatchesQuery } from "@/v2-backoffice-client/license/account/view/__types__/license.account.matches.query"

type MatchRow = LicenseAccountMatchesQuery["getBillingAccountMatches"][number]

function createMatchColumns(account: LicenseAccountFragment) {
  return createColumns<MatchRow>({
    enableSorting: true,
    enableHiding: true,
    columns: {
      CustomerName: {
        id: "CustomerName",
        label: "Customer",
        description:
          "A customer record carrying this account's org number — opens its panel.",
        defaultSize: 220,
        getValue: (row) => row.CustomerName ?? "",
        renderCell({ row }) {
          const { CustomerName, IsCurrentLink } = row.original
          return (
            <span className="flex items-center gap-2">
              <span className="truncate">
                {CustomerName ?? "Unnamed customer"}
              </span>
              {IsCurrentLink && <Badge variant="success">Current link</Badge>}
            </span>
          )
        },
        to: ({ row }) => {
          const externalId = row.original.ExternalCustomerId
          if (!externalId) return
          return `>>?v=customer:${externalId}`
        },
      },
      Stage: {
        id: "Stage",
        label: "Stage",
        description:
          "The customer's lifecycle stage from HubSpot — a real customer usually beats a lead-stage shell.",
        defaultSize: 100,
        enableGrouping: true,
        getValue: (row) => row.Stage ?? "",
      },
      UserCount: {
        id: "UserCount",
        label: "Users",
        description:
          "Live (non-deleted) users owned by this customer record — the strongest signal for which duplicate is the real one. 0 means an empty shell.",
        defaultSize: 70,
        getValue: (row) => String(row.UserCount),
        renderCell({ row }) {
          return row.original.UserCount
        },
      },
      ActiveUserCount: {
        id: "ActiveUserCount",
        label: "Active (30d)",
        description: "Users with a successful sign-in inside the last 30 days.",
        defaultSize: 90,
        getValue: (row) => String(row.ActiveUserCount),
        renderCell({ row }) {
          return row.original.ActiveUserCount
        },
        summaryValue({ row }) {
          return row.original.ActiveUserCount
        },
      },
      LastActivityAt: {
        id: "LastActivityAt",
        label: "Last Activity",
        description: "The most recent successful sign-in across its users.",
        defaultSize: 100,
        renderCell({ row }) {
          const value = row.original.LastActivityAt
          return value ? formatDateString(value) : null
        },
        exportValue({ row }) {
          return formatDateString(row.original.LastActivityAt)
        },
      },
      LiveLicenses: {
        id: "LiveLicenses",
        label: "Live Licenses",
        description:
          "Licenses in a covering status (trial / active / scheduled / not renewing).",
        defaultSize: 100,
        getValue: (row) => String(row.LiveLicenses),
        renderCell({ row }) {
          return row.original.LiveLicenses
        },
        summaryValue({ row }) {
          return row.original.LiveLicenses
        },
      },
      LinkedAccountName: {
        id: "LinkedAccountName",
        label: "Holds Account",
        description:
          "The Fenerum account this customer is already linked to — a customer bills through at most one account, so it cannot take this one too.",
        defaultSize: 160,
        getValue: (row) => row.LinkedAccountName ?? "",
        renderCell({ row }) {
          const { IsCurrentLink, LinkedAccountName } = row.original
          if (IsCurrentLink) return null
          return LinkedAccountName ?? null
        },
      },
      CustomerCreatedAt: {
        id: "CustomerCreatedAt",
        label: "Created",
        description:
          "When the customer record was created — merge-leftover shells are often the newer row.",
        defaultSize: 95,
        renderCell({ row }) {
          const value = row.original.CustomerCreatedAt
          return value ? formatDateString(value) : null
        },
        exportValue({ row }) {
          return formatDateString(row.original.CustomerCreatedAt)
        },
      },
      Domain: {
        id: "Domain",
        label: "Domain",
        defaultSize: 140,
        defaultHidden: true,
        getValue: (row) => row.Domain ?? "",
      },
      ExternalCustomerId: {
        id: "ExternalCustomerId",
        label: "HubSpot Id",
        defaultSize: 110,
        defaultHidden: true,
        to: ({ value }) => {
          if (!value) return
          return `${HUBSPOT_BASE_URL}/${value}`
        },
      },
      CustomerId: {
        id: "CustomerId",
        label: "Id",
        defaultSize: 60,
        defaultHidden: true,
      },
      Action: {
        id: "Action",
        label: "",
        defaultSize: 90,
        enableSorting: false,
        renderCell({ row }) {
          const match = row.original
          if (match.IsCurrentLink) {
            return (
              <LicenseAccountLinkButton
                accountId={account.Id}
                accountName={account.CompanyName}
                customerId={null}
                customerName={match.CustomerName}
              />
            )
          }
          if (match.LinkedAccountName) {
            return <span className="text-xs text-muted-foreground">Taken</span>
          }
          return (
            <LicenseAccountLinkButton
              accountId={account.Id}
              accountName={account.CompanyName}
              customerId={match.CustomerId}
              customerName={match.CustomerName}
            />
          )
        },
        exportValue() {
          return ""
        },
      },
    },
  })
}

export type LicenseAccountMatchTableProps = Omit<
  TableProps<MatchRow>,
  "data" | "columns"
> & {
  account: LicenseAccountFragment
  matches: MatchRow[]
  isLoading?: boolean
  error?: Error
  onRefresh?(): void
}

// The candidates table on the account panel's Customers tab: every customer
// carrying the account's org number, usage carriers first (the proc's
// order), with a Link action per free candidate and Unlink on the current
// one. Presentational only — the match panel owns the query.
function LicenseAccountMatchTable(props: LicenseAccountMatchTableProps) {
  const {
    className,
    account,
    matches,
    isLoading,
    error,
    onRefresh,
    ...tableProps
  } = props

  const columns = useMemo(() => createMatchColumns(account), [account])

  return (
    <Table
      className={cn("license-account-match-table", className)}
      tableId="license-account-match-table"
      data={matches}
      columns={columns}
      {...tableProps}
    >
      <Table.Toolbar>
        {!account.IsArchived && (
          <LicenseAccountLinkPicker
            accountId={account.Id}
            accountName={account.CompanyName}
          />
        )}
        <div className="flex-1" />
        <Table.ToolbarMenu
          onRefresh={onRefresh}
          exportFileName="billing-account-matches"
        />
      </Table.Toolbar>

      <Table.Content
        isLoading={isLoading}
        error={error}
        renderEmpty={
          <p className="p-4 text-center text-sm text-muted-foreground">
            No customer carries this org number. Fix the number on the customer
            record (or in Fenerum) and the hourly mirror links it on its own —
            or link a customer directly via the search above.
          </p>
        }
      />
    </Table>
  )
}

export default LicenseAccountMatchTable
